import { cva, type VariantProps } from 'class-variance-authority';

/**
 * InfoCard outer content layout (the `CardContent` wrapper).
 *
 * Controls padding and the alignment of stacked blocks (image + the inner
 * content group). The gap here is the *outer* gap — it only separates an
 * optional image from the content group, matching the Figma "Info Card"
 * structure where the header group is a single child of the card body.
 *
 * `default` keeps the compact `info-card-*` padding tokens used in feature
 * grids and sidebar highlights. `lg` maps to the Figma "Info Card"
 * (large-format) spec — `info-box-lg-*` padding (32px sides / 28px top-bottom).
 */
export const infoCardContentVariants = cva('flex flex-col', {
  variants: {
    size: {
      default: 'px-info-card-sides py-info-card-top-bottom gap-sm',
      lg: 'px-info-box-lg-sides py-info-box-lg-top-bottom gap-lg',
    },
    align: {
      start: 'items-start text-left',
      center: 'items-center text-center',
    },
  },
  defaultVariants: {
    size: 'default',
    align: 'start',
  },
});

/**
 * InfoCard inner content group (the Figma "Headers (H1-H6)" group).
 *
 * Wraps brow + heading + body + cta so they share the tight `gap-xxs` (8px)
 * rhythm the Figma "Info Card" uses between text elements — distinct from the
 * larger outer gap that separates an image from this group.
 */
export const infoCardGroupVariants = cva('flex flex-col w-full', {
  variants: {
    size: {
      default: 'gap-sm',
      lg: 'gap-xxs',
    },
    align: {
      start: 'items-start text-left',
      center: 'items-center text-center',
    },
  },
  defaultVariants: {
    size: 'default',
    align: 'start',
  },
});

export type InfoCardContentVariantProps = VariantProps<
  typeof infoCardContentVariants
>;
